/**
 * SandboxBanner — banner topo "MODO SANDBOX" (PR #98, expandido PR #99).
 *
 * Aparece quando sandbox está ativo (lógica do backend em `sandbox.py`):
 *   - APP_ENV=prod + ACORDEI_SANDBOX_MODE=1 → ON (sandbox explícito em prod)
 *   - APP_ENV != prod sem env var → ON (auto-on em dev por DX safety, PR #99)
 *
 * Detectado via GET /api/v1/health/sandbox. O backend retorna `razao` que
 * usamos pra customizar a mensagem (env_var_explicita_on vs auto_dev_on).
 *
 * Visibilidade (decidido pelo user):
 *  - SEMPRE em rotas internas (admin, mediador, minha-conta, acompanhar)
 *  - NÃO mostra em rotas públicas puramente landing (/, /sobre, /planos, /faq)
 *
 * Polling a cada 60s pra detectar quando user desliga ACORDEI_SANDBOX_MODE
 * (banner some sem reload).
 */
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { AlertTriangle, X } from "lucide-react";

const SANDBOX_POLL_INTERVAL_MS = 60_000;

type SandboxInfo = {
  sandbox: boolean;
  razao: "env_var_explicita_on" | "env_var_explicita_off" | "auto_prod_off" | "auto_dev_on";
  app_env: string;
};

const ROTAS_INTERNAS_PREFIX = [
  "/admin",
  "/minha-conta",
  "/acompanhar",
  "/resultado",
  "/acordo",
  "/configuracoes",
];

function _rotaInterna(path: string): boolean {
  return ROTAS_INTERNAS_PREFIX.some((p) => path.startsWith(p));
}

export default function SandboxBanner() {
  const [location] = useLocation();
  const [info, setInfo] = useState<SandboxInfo | null>(null);
  const [fechadoUsuario, setFechadoUsuario] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function checar() {
      try {
        const resp = await fetch("/api/v1/health/sandbox", {
          headers: { Accept: "application/json" },
        });
        if (!resp.ok) return;
        const data = (await resp.json()) as SandboxInfo;
        if (!cancelled) {
          setInfo(data);
        }
      } catch {
        /* sem rede / endpoint indisponível — ignora */
      }
    }

    void checar();
    const interval = setInterval(checar, SANDBOX_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Só renderiza em rota interna + sandbox ativo + não fechado pelo user
  if (!info?.sandbox || fechadoUsuario || !_rotaInterna(location)) return null;

  // PR #99: cor + mensagem por razão
  // - auto_dev_on (dev local) → azul mais discreto, msg sobre APP_ENV
  // - env_var_explicita_on (prod) → âmbar urgente, msg sobre dados reais protegidos
  const ehDev = info.razao === "auto_dev_on";
  const bgGradient = ehDev
    ? "linear-gradient(90deg, #60a5fa, #3b82f6)"
    : "linear-gradient(90deg, #fbbf24, #f59e0b)";
  const textColor = ehDev ? "#ffffff" : "#0f172a";
  const titulo = ehDev ? "MODO DEV (sandbox automático)" : "MODO SANDBOX (prod)";
  const subtitulo = ehDev
    ? `APP_ENV=${info.app_env} · todos services externos mockados`
    : "Nenhum contato externo é feito · simulação E2E sem réu real";

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-50 flex items-center justify-center gap-3 px-4 py-2 text-sm font-semibold"
      style={{
        background: bgGradient,
        color: textColor,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
    >
      <AlertTriangle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
      <span className="hidden sm:inline">{titulo}</span>
      <span className="inline sm:hidden">{ehDev ? "DEV" : "SANDBOX"}</span>
      <span className="hidden md:inline opacity-80">· {subtitulo}</span>
      <button
        type="button"
        onClick={() => setFechadoUsuario(true)}
        aria-label="Fechar banner sandbox (volta no próximo carregamento)"
        className="ml-2 p-0.5 rounded hover:bg-black/10 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
